<script lang="ts">
  import { push } from 'svelte-spa-router';
  import Button from '../../../components/Button.svelte';
  import StageBadge from '../../../components/StageBadge.svelte';
  import Stepper from '../../../components/Stepper.svelte';
  import { stageFromStatusName, type LifecycleStage } from '../../../lib/donationLifecycle';
  import EmployeeDonationService, { type DonationRequest } from '../../../lib/api/employee/donation.service';
  import BackButton from '../../../components/BackButton.svelte';
  import Notification from '../../../components/Notification.svelte';
  import EditCharityModal from './EditCharityModal.svelte';
  import CancelDonationModal from './CancelDonationModal.svelte';
  import toast from 'svelte-french-toast';

  export let donationId: string = '';
  
  let loading = false;
  let error: string | null = null;
  let donation: DonationRequest | null = null;
  let loadRequestId = 0;

  // Modal state
  let showEditModal = false;
  let showCancelModal = false;

  // Notification state
  let notificationShow = false;
  let notificationType: 'success' | 'error' = 'success';
  let notificationMessage = '';

  // Check if donation is in pending status
  $: isPending = donation?.donation_request_status?.name?.toLowerCase() === 'pending';

  // Rejected requests have no authorisation document.
  $: isRejected = donation?.donation_request_status?.name?.toLowerCase() === 'rejected';

  // Canonical lifecycle stage (prefer backend value; fall back to status name).
  $: stage = ((donation?.lifecycle_stage as LifecycleStage) || stageFromStatusName(donation?.donation_request_status?.name));

  $: canDownloadPdf = !!(donation?.has_authorisation_pdf) && !isRejected;

  let downloading = false;

  async function handleDownload() {
    if (!donation?.uuid) return;
    downloading = true;
    try {
      await EmployeeDonationService.downloadAuthorisationPdf(
        donation.uuid,
        `Leave-Cashout-Authorisation-${donation.uuid}.pdf`
      );
      toast.success('Download started');
    } catch (err) {
      console.error('Error downloading PDF:', err);
      toast.error('Download failed. The authorisation PDF may not be available yet.');
    } finally {
      downloading = false;
    }
  }

  function showNotification(message: string, type: 'success' | 'error' = 'success') {
    notificationMessage = message;
    notificationType = type;
    notificationShow = true;
    setTimeout(() => notificationShow = false, 4000);
  }

  function handleEditClick() {
    showEditModal = true;
  }

  function handleCancelClick() {
    showCancelModal = true;
  }

  function handleEditSuccess(event: CustomEvent) {
    showEditModal = false;
    showNotification(event.detail.message, 'success');
    donation = null;
    loadDonationDetails(donationId);
  }

  function handleCancelSuccess(event: CustomEvent) {
    showCancelModal = false;
    showNotification(event.detail.message, 'success');
    // Navigate to dashboard after cancellation
    setTimeout(() => {
      push('/');
    }, 1500);
  }

  async function loadDonationDetails(id: string) {
    if (!id) {
      error = 'No donation ID provided';
      loading = false;
      return;
    }

    if (donation?.uuid === id) {
      return;
    }

    const requestId = ++loadRequestId;
    loading = true;
    error = null;
    
    try {
      const response = await EmployeeDonationService.getDonationRequest(id);

      if (requestId !== loadRequestId) {
        return;
      }
      
      if (response.success && response.data) {
        donation = response.data;
      } else {
        donation = null;
        error = response.message || 'Failed to load donation details';
        if ((response as { statusCode?: number }).statusCode === 401) {
          push('/login');
          return;
        }
      }
    } catch (err: unknown) {
      if (requestId !== loadRequestId) {
        return;
      }
      donation = null;
      error = err instanceof Error ? err.message : 'Failed to load donation details';
    } finally {
      if (requestId === loadRequestId) {
        loading = false;
      }
    }
  }

  function formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    return EmployeeDonationService.formatDate(dateString);
  }

  $: if (donationId) {
    if (donation?.uuid !== donationId) {
      loadDonationDetails(donationId);
    }
  } else if (!loading && !donation) {
    error = 'No donation ID provided';
  }
</script>

<div class="donation-detail-page">
  <BackButton text="Back" onClick={() => window.history.back()} />
  
  {#if loading}
    <div class="loading-container">
      <div class="loading-spinner"></div>
      <p>Loading donation details...</p>
    </div>
  {:else if error}
    <div class="error-container">
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="error-icon">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="12"></line>
        <line x1="12" y1="16" x2="12.01" y2="16"></line>
      </svg>
      <h3 class="error-title">Failed to Load Donation Details</h3>
      <p class="error-message">{error}</p>
      <div class="error-actions">
        <Button variant="secondary" on:click={() => window.history.back()}>Back</Button>
        <Button variant="primary" on:click={() => loadDonationDetails(donationId)}>Try Again</Button>
      </div>
    </div>
  {:else if donation}
    <div class="detail-container">
      <!-- Donation Request Information -->
      <div class="info-section">
        <h2>Donation Request Details</h2>
        <div class="stepper-wrapper">
          <Stepper {stage} />
        </div>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">Status</span>
            <div class="status-wrapper">
              <StageBadge {stage} />
            </div>
          </div>
          <div class="info-item">
            <span class="info-label">Hours Requested</span>
            <span class="value-large">{donation.leave_hours} hours</span>
          </div>
          <div class="info-item">
            <span class="info-label">Request Date</span>
            <span>{formatDate(donation.created_at)}</span>
          </div>
          {#if donation.approved_at}
            <div class="info-item">
              <span class="info-label">Approved Date</span>
              <span>{formatDate(donation.approved_at)}</span>
            </div>
          {/if}
          {#if isRejected && donation.rejected_at}
            <div class="info-item">
              <span class="info-label">Rejected Date</span>
              <span>{formatDate(donation.rejected_at)}</span>
            </div>
          {/if}
        </div>
      </div>

      {#if isRejected}
        <div class="info-section rejection-section">
          <h2>Reason for Rejection</h2>
          <div class="rejection-notice">
            {#if donation.rejection_reason?.trim()}
              <p>{donation.rejection_reason}</p>
            {:else}
              <p class="rejection-fallback">
                No specific reason was provided. Please contact your employer's HR representative for more information.
              </p>
            {/if}
          </div>
        </div>
      {/if}

      <!-- Charity Information Section -->
      <div class="info-section">
        <h2>Charity Information</h2>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">Charity Name</span>
            {#if donation.charity?.uuid}
              <span>
                <button 
                  class="clickable-name"
                  on:click={() => donation?.charity?.uuid && push(`/view-charity/${donation.charity.uuid}`)}
                  on:keydown={(e) => e.key === 'Enter' && donation?.charity?.uuid && push(`/view-charity/${donation.charity.uuid}`)}
                  aria-label="View charity details"
                  type="button"
                >
                  {donation.charity.name}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M5 12h14M12 5l7 7-7 7"></path>
                  </svg>
                </button>
              </span>
            {:else}
              <span>{donation.charity?.name || 'N/A'}</span>
            {/if}
          </div>
          {#if donation.charity?.abn || donation.charity?.charity_government_id}
            <div class="info-item">
              <span class="info-label">ABN</span>
              <span>{donation.charity.abn || donation.charity.charity_government_id}</span>
            </div>
          {/if}
          {#if donation.charity?.category?.name}
            <div class="info-item">
              <span class="info-label">Category</span>
              <span>{donation.charity.category.name}</span>
            </div>
          {/if}
          {#if donation.charity?.website}
            <div class="info-item">
              <span class="info-label">Website</span>
              <span>
                <a href={donation.charity.website} target="_blank" rel="noopener noreferrer" class="website-link">
                  {donation.charity.website}
                </a>
              </span>
            </div>
          {/if}
          {#if donation.charity?.contact_email}
            <div class="info-item">
              <span class="info-label">Contact Email</span>
              <span>
                <a href="mailto:{donation.charity.contact_email}" class="email-link">
                  {donation.charity.contact_email}
                </a>
              </span>
            </div>
          {/if}
          {#if donation.charity?.contact_phone}
            <div class="info-item">
              <span class="info-label">Contact Phone</span>
              <span>
                <a href="tel:{donation.charity.contact_phone}" class="phone-link">
                  {donation.charity.contact_phone}
                </a>
              </span>
            </div>
          {/if}
        </div>
      </div>

      <!-- Payment Information Section -->
      {#if donation.payment_status && donation.payment_status !== 'not_paid'}
      <div class="info-section payment-info-section">
        <h2>Payment Information</h2>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">Payment Status</span>
            <div class="payment-status-badge payment-status-{donation.payment_status}">
              {donation.payment_status === 'completed' ? 'Completed' : donation.payment_status === 'paid' ? 'Paid' : 'Not Paid'}
            </div>
          </div>
          {#if donation.employer_payment_date}
          <div class="info-item">
            <span class="info-label">Employer Payment Date</span>
            <span>{formatDate(donation.employer_payment_date)}</span>
          </div>
          {/if}
          {#if donation.employer_payment_reference}
          <div class="info-item">
            <span class="info-label">Payment Reference</span>
            <span class="reference-value">{donation.employer_payment_reference}</span>
          </div>
          {/if}
          {#if donation.charity_payment_date}
          <div class="info-item">
            <span class="info-label">Charity Payment Date</span>
            <span>{formatDate(donation.charity_payment_date)}</span>
          </div>
          {/if}
          {#if donation.payout_uuid}
          <div class="info-item">
            <span class="info-label">Payout ID</span>
            <span class="reference-value">#{donation.payout_uuid.substring(0, 8).toUpperCase()}</span>
          </div>
          {/if}
        </div>
      </div>
      {/if}

      <!-- Employer Information Section -->
      <div class="info-section">
        <h2>Employer Information</h2>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">Employer Name</span>
            {#if donation.employer?.uuid}
              <span>
                <button 
                  class="clickable-name"
                  on:click={() => donation?.employer?.uuid && push(`/view-employer/${donation.employer.uuid}`)}
                  on:keydown={(e) => e.key === 'Enter' && donation?.employer?.uuid && push(`/view-employer/${donation.employer.uuid}`)}
                  aria-label="View employer details"
                  type="button"
                >
                  {donation.employer.name}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M5 12h14M12 5l7 7-7 7"></path>
                  </svg>
                </button>
              </span>
            {:else}
              <span>{donation.employer?.name || 'N/A'}</span>
            {/if}
          </div>
          {#if donation.employer?.abn}
            <div class="info-item">
              <span class="info-label">ABN</span>
              <span>{donation.employer.abn}</span>
            </div>
          {/if}
          {#if donation.employer?.contact_email}
            <div class="info-item">
              <span class="info-label">Contact Email</span>
              <span>
                <a href="mailto:{donation.employer.contact_email}" class="email-link">
                  {donation.employer.contact_email}
                </a>
              </span>
            </div>
          {/if}
          {#if donation.employer?.contact_phone}
            <div class="info-item">
              <span class="info-label">Contact Phone</span>
              <span>
                <a href="tel:{donation.employer.contact_phone}" class="phone-link">
                  {donation.employer.contact_phone}
                </a>
              </span>
            </div>
          {/if}
          {#if donation.employer?.head_office_location}
            <div class="info-item">
              <span class="info-label">Head Office Location</span>
              <span>{donation.employer.head_office_location}</span>
            </div>
          {/if}
        </div>
      </div>

      <!-- Download authorisation PDF (when available) -->
      {#if canDownloadPdf}
        <div class="actions-section actions-section-download">
          <h2 class="actions-heading">Actions</h2>
          <div class="actions-container">
            <Button
              variant="secondary"
              on:click={handleDownload}
              disabled={downloading}
            >
              <span class="button-content">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                {downloading ? 'Downloading…' : 'Download authorisation PDF'}
              </span>
            </Button>
          </div>
        </div>
      {/if}

      <!-- Action Buttons for Pending Requests -->
      {#if isPending}
        <div class="actions-section">
          <div class="actions-container">
            <Button 
              variant="secondary" 
              on:click={handleEditClick}
            >
              <span class="button-content">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
                Change Charity
              </span>
            </Button>
            <Button 
              variant="secondary" 
              on:click={handleCancelClick}
            >
              <span class="button-content cancel-button-content">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
                Cancel Request
              </span>
            </Button>
          </div>
          <p class="actions-note">
            You can change the charity or cancel this request while it's pending approval.
          </p>
        </div>
      {/if}
    </div>
  {/if}
</div>

<!-- Notification -->
<Notification 
  show={notificationShow} 
  type={notificationType} 
  message={notificationMessage} 
/>

<!-- Edit Charity Modal -->
<EditCharityModal
  open={showEditModal}
  donationUuid={donationId}
  currentCharityUuid={donation?.charity?.uuid || ''}
  currentCharityName={donation?.charity?.name || ''}
  on:close={() => showEditModal = false}
  on:success={handleEditSuccess}
/>

<!-- Cancel Donation Modal -->
<CancelDonationModal
  open={showCancelModal}
  donationUuid={donationId}
  charityName={donation?.charity?.name || ''}
  leaveHours={donation?.leave_hours || 0}
  on:close={() => showCancelModal = false}
  on:success={handleCancelSuccess}
/>

<style>
  .donation-detail-page {
    padding: 2rem;
    max-width: 1000px;
    margin: 0 auto;
    background: #f7fafc;
    min-height: 100vh;
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .loading-spinner {
    width: 2rem;
    height: 2rem;
    border: 3px solid #f3f4f6;
    border-top: 3px solid var(--color-primary, #f0b429);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    text-align: center;
  }

  .error-icon {
    color: var(--color-negative, #dc2626);
    margin-bottom: 1rem;
  }

  .error-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.5rem 0;
  }

  .error-message {
    color: #6b7280;
    font-size: 1rem;
    margin-bottom: 1.5rem;
    max-width: 500px;
  }

  .error-actions {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
    justify-content: center;
  }

  .detail-container {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .info-section {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .info-section h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 1.5rem 0;
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1rem;
  }

  .info-item {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .info-item .info-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
  }

  .info-item span {
    font-size: 1rem;
    color: #111827;
    font-weight: 400;
  }

  .value-large {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--color-positive, #059669);
  }

  .status-wrapper {
    display: inline-flex;
  }

  .stepper-wrapper {
    margin: 0 0 1.75rem 0;
  }

  .rejection-section h2 {
    margin-bottom: 1rem;
  }

  .rejection-notice {
    background-color: #fee2e2;
    border-left: 4px solid #dc2626;
    border-radius: 8px;
    padding: 1rem 1.25rem;
  }

  .rejection-notice p {
    margin: 0;
    font-size: 0.875rem;
    color: #111827;
    line-height: 1.6;
  }

  .rejection-fallback {
    color: #6b7280;
  }

  .website-link,
  .email-link,
  .phone-link {
    color: var(--color-primary, #3b82f6);
    text-decoration: none;
    transition: color 0.2s;
  }

  .website-link:hover,
  .email-link:hover,
  .phone-link:hover {
    color: var(--color-primary-hover, #2563eb);
    text-decoration: underline;
  }

  .clickable-name {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    background: none;
    border: none;
    padding: 0;
    color: var(--color-primary, #3b82f6);
    font-size: 1rem;
    font-weight: 400;
    cursor: pointer;
    text-decoration: none;
    transition: all 0.2s;
  }

  .clickable-name:hover {
    color: var(--color-primary-hover, #2563eb);
    text-decoration: underline;
  }

  .clickable-name:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    border-radius: 4px;
  }

  .clickable-name svg {
    flex-shrink: 0;
    opacity: 0.7;
    transition: opacity 0.2s, transform 0.2s;
  }

  .clickable-name:hover svg {
    opacity: 1;
    transform: translate(2px, -2px);
  }

  .payment-info-section {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
  }

  .payment-status-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.375rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.875rem;
    font-weight: 500;
    white-space: nowrap;
  }

  .payment-status-completed {
    background-color: #d1fae5;
    color: #065f46;
  }

  .payment-status-paid {
    background-color: #dbeafe;
    color: #1e40af;
  }

  .payment-status-not_paid {
    background-color: #f3f4f6;
    color: #6b7280;
  }

  .reference-value {
    font-family: monospace;
    color: #6366f1;
    font-weight: 500;
  }

  /* Action Buttons Section */
  .actions-section {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    border: 1px solid #fef3c7;
    background-color: #fffbf5;
  }

  .actions-section-download {
    border-color: #e5e7eb;
    background-color: #fff;
  }

  .actions-heading {
    font-size: 1.25rem;
    font-weight: 600;
    color: #2c3e50;
    margin: 0 0 1rem 0;
  }

  .actions-container {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .button-content {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
  }

  .cancel-button-content svg {
    color: #ef4444;
  }

  .actions-note {
    margin: 1rem 0 0;
    font-size: 0.875rem;
    color: #6b7280;
  }

  @media (max-width: 768px) {
    .donation-detail-page {
      padding: 1rem;
    }

    .info-grid {
      grid-template-columns: 1fr;
    }

    .actions-container {
      flex-direction: column;
    }
  }
</style>

